/**
 * Risk Metrics Card Component
 * Displays risk-related metrics in a DataTable: Sharpe ratio, Max Drawdown, Risk/Reward Ratio
 */

import React, { useMemo } from "react";
import { Text, useColorModeValue } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import { TradeStatisticsFilters } from "../../../../types/trade";
import { useRiskMetrics } from "../../../../hooks/useTradeStatistics";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { DataTable } from "../../../../components/ui/data-table";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import { StatisticsCardHeadline, STATS_FONT_FAMILY } from "./statisticsCardHeadline";
import { STATISTICS_NO_DATA_TIMEFRAME_MESSAGE } from "./statisticsChartTooltip";
import { riskMetricsHasData } from "./riskMetricsUtils";
import { statisticsShadcnCardClassName, statisticsShadcnCardContentClassName } from "./statisticsShadcnCardShell";

interface RiskMetricsCardProps {
  filters?: TradeStatisticsFilters;
}

interface RiskMetricRow {
  metric: string;
  value: string;
}

const RiskMetricsCard: React.FC<RiskMetricsCardProps> = ({ filters }) => {
  const { formatMoney } = useDisplayCurrency();
  const cardBg = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const { data } = useRiskMetrics(filters);

  const tableData: RiskMetricRow[] = useMemo(() => {
    if (!data) {
      return [];
    }

    const formatRatio = (v: number | null): string => (v === null ? "N/A" : v.toFixed(2));

    return [
      { metric: "Sharpe Ratio", value: formatRatio(data.sharpe_ratio) },
      { metric: "Max Drawdown", value: data.max_drawdown !== null ? formatMoney(data.max_drawdown) : "N/A" },
      { metric: "Risk/Reward Ratio", value: data.risk_reward_ratio !== null ? `${formatRatio(data.risk_reward_ratio)}:1` : "N/A" },
    ];
  }, [data, formatMoney]);

  const columns: ColumnDef<RiskMetricRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "metric",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
            {row.getValue("metric")}
          </span>
        ),
      },
      {
        accessorKey: "value",
        header: "Value",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
            {row.getValue("value")}
          </span>
        ),
      },
    ],
    [],
  );

  if (!riskMetricsHasData(data)) {
    return (
      <Card bg={cardBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
        <CardBody>
          <StatisticsCardHeadline mb={3}>Risk Metrics</StatisticsCardHeadline>
          <Text fontSize="sm" color="gray.500" fontFamily={STATS_FONT_FAMILY}>
            {STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
          </Text>
        </CardBody>
      </Card>
    );
  }

  return (
    <div
      data-statistics-shadcn-card=""
      className={statisticsShadcnCardClassName}
    >
      <div className={statisticsShadcnCardContentClassName}>
        <StatisticsCardHeadline>Risk Metrics</StatisticsCardHeadline>
        <div className="mt-3">
          <DataTable
            columns={columns}
            data={tableData}
            showPagination={false}
            emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
          />
        </div>
      </div>
    </div>
  );
};

export default RiskMetricsCard;
